import {Component, computed, effect, inject, input, output, ChangeDetectionStrategy} from "@angular/core"
import {MatTooltipModule} from "@angular/material/tooltip"
import {cardAnimations} from "@app/common/components/cards/animations"
import {BaseSelectComponent} from "@app/platform/components/base/base-select/base-select.component"
import {TemplateAddTemplateItemFragment} from "@app/template-editor/components/template-add-product/template-add-product.generated"
import {SceneManagerService} from "@app/template-editor/services/scene-manager.service"
import {Matrix4} from "@cm/math"
import {Parameters, TemplateInstance, TemplateReference} from "@cm/template-nodes"
import {CardErrorComponent} from "@common/components/cards/card-error/card-error.component"
import {CardPlaceholderComponent} from "@common/components/cards/card-placeholder/card-placeholder.component"
import {InfiniteListComponent} from "@common/components/lists/infinite-list/infinite-list.component"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {AuthService} from "@common/services/auth/auth.service"
import {TemplateFilterInput, TemplateType} from "@generated"
import {TemplateAddTemplateItemsGQL} from "@template-editor/components/template-add-product/template-add-product.generated"
import {v4 as uuid4} from "uuid"
import {TemplateAddCardComponent} from "@app/template-editor/components/template-add-card/template-add-card.component"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-template-add-other-system-template",
    templateUrl: "./template-add-other-system-template.component.html",
    styleUrl: "./template-add-other-system-template.component.scss",
    animations: [...cardAnimations],
    imports: [InfiniteListComponent, CardErrorComponent, TemplateAddCardComponent, CardPlaceholderComponent, MatTooltipModule],
})
export class TemplateAddOtherSystemTemplate extends BaseSelectComponent<TemplateAddTemplateItemFragment, TemplateFilterInput> {
    readonly $search = input.required<string>({alias: "search"})
    readonly templatesGql = inject(TemplateAddTemplateItemsGQL)
    private readonly sceneManagerService = inject(SceneManagerService)
    private rawData = this.implementDataLoader(fetchThrowingErrors(this.templatesGql))
    readonly $data = computed(() => {
        const currentTemplateId = this.sceneManagerService.$templateId()
        return this.rawData()
            .items.filter((item) => !item.data || item.data.id !== currentTemplateId)
            .map((item) => ({...item, node: item.data ? this.getTemplateInstance(item.data) : undefined}))
    })
    readonly onItemClicked = output()

    private readonly auth = inject(AuthService)

    constructor() {
        super()
        this.$customFilters.set({type: [TemplateType.System]})

        effect(() => {
            this.$lockOrganization.set(!this.auth.$actingAsStaff())
        })

        effect(() => {
            const search = this.$search()
            this.searchText$.next(search)
        })
    }

    private getTemplateInstance(data: TemplateAddTemplateItemFragment) {
        const templateRevisionId = data.latestRevision?.legacyId
        if (templateRevisionId === undefined) {
            return undefined
        }

        return new TemplateInstance({
            parameters: {
                id: uuid4(),
                name: data.name ?? "Template Instance",
                lockedTransform: Matrix4.identity().toArray(),
                visible: true,
                template: new TemplateReference({
                    parameters: {
                        templateId: data.legacyId,
                        templateRevisionId: null,
                    },
                }),
                parameters: new Parameters({parameters: {}}),
            },
        })
    }
}
